import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';

function usePulseOpacity() {
  const [opacity, setOpacity] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => {
      const t = (Date.now() - start) / 1000;
      setOpacity((Math.sin(2 * Math.PI * 0.375 * t) + 1) / 8);
    }, 50);
    return () => clearInterval(id);
  }, []);
  return opacity;
}

function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/* A single movable point inside the accordion */
function AccordionPoint({ dataX, dataY, axis, parentSize, color, size, bounds, index, onMove }) {
  const thumbRef = useRef(null);
  const pulseOpacity = usePulseOpacity();
  const diameter = (size / 0.7) * 15 * Math.max(parentSize.width, parentSize.height) / 215;
  const travelX  = Math.max(parentSize.width  - diameter, 1);
  const travelY  = Math.max(parentSize.height - diameter, 1);

  const px = ((dataX - axis.min_x) / (axis.max_x - axis.min_x)) * travelX;
  const py = travelY - ((dataY - axis.min_y) / (axis.max_y - axis.min_y)) * travelY;

  /* Convert pixel bounds (in data space) to drag bounds (in pixel space) */
  const dragBounds = {
    left:   ((bounds.left  - axis.min_x) / (axis.max_x - axis.min_x)) * travelX,
    right:  ((bounds.right - axis.min_x) / (axis.max_x - axis.min_x)) * travelX,
    top:    travelY - ((bounds.top    - axis.min_y) / (axis.max_y - axis.min_y)) * travelY,
    bottom: travelY - ((bounds.bottom - axis.min_y) / (axis.max_y - axis.min_y)) * travelY,
  };

  const handleDrag = (_, pos) => {
    const newX = (pos.x / travelX) * (axis.max_x - axis.min_x) + axis.min_x;
    const newY = ((travelY - pos.y) / travelY) * (axis.max_y - axis.min_y) + axis.min_y;
    onMove(newX, newY, index);
  };

  return (
    <Draggable
      nodeRef={thumbRef}
      position={{ x: px, y: py }}
      bounds={dragBounds}
      onDrag={handleDrag}
    >
      <div ref={thumbRef} style={{ width: diameter, height: diameter, position: 'absolute', cursor: 'grab', pointerEvents: 'auto' }}>
        <svg width="100%" height="100%" viewBox="-0.5 -0.5 1 1">
          <circle cx={0} cy={0} r={0.5} fill={hexToRgba(color, pulseOpacity)} />
          <circle cx={0} cy={0} r={0.25} fill={color} />
        </svg>
      </div>
    </Draggable>
  );
}

/**
 * Accordion: array of constrained draggable points.
 * @param {{ xs, ys, axis, color, size, onChange }} props
 */
export default function IntFigAccordion({ xs, ys, axis, color = '#1f76D2', size = 1.0, onChange }) {
  const containerRef = useRef(null);
  const [parentSize, setParentSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) setParentSize({ width, height });
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const gap = (axis.max_x - axis.min_x) / 20;
  const n = xs.length;

  const onMove = (newX, newY, index) => {
    const newXs = [...xs];
    const newYs = [...ys];
    newXs[index] = newX;
    newYs[index] = newY;
    onChange(newXs, newYs);
  };

  if (parentSize.width === 0 || parentSize.height === 0) {
    return <div ref={containerRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} />;
  }

  return (
    <div ref={containerRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {xs.map((x, i) => {
        let bounds;
        if (i === 0 || i === n - 1) {
          bounds = { left: xs[i], right: xs[i], top: axis.max_y, bottom: axis.min_y };
        } else {
          bounds = { left: xs[i - 1] + gap, right: xs[i + 1] - gap, top: axis.max_y, bottom: axis.min_y };
        }
        return (
          <AccordionPoint
            key={i}
            dataX={xs[i]} dataY={ys[i]}
            axis={axis}
            parentSize={parentSize}
            color={color}
            size={size}
            bounds={bounds}
            index={i}
            onMove={onMove}
          />
        );
      })}
    </div>
  );
}
